import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ContentPanel = ({ content, onContentUpdate }) => {
  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPlatformIcon = (platform) => {
    const platformIcons = {
      'Facebook': 'Facebook',
      'Twitter': 'Twitter',
      'LinkedIn': 'Linkedin',
      'Instagram': 'Instagram',
      'Website': 'Globe'
    };
    return platformIcons?.[platform] || 'Globe';
  };

  const getPlatformColor = (platform) => {
    const platformColors = {
      'Facebook': 'text-blue-600',
      'Twitter': 'text-sky-500',
      'LinkedIn': 'text-blue-700',
      'Instagram': 'text-pink-600',
      'Website': 'text-green-600'
    };
    return platformColors?.[platform] || 'text-gray-600';
  };

  return (
    <div className="bg-card border border-border rounded-lg h-full">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Original Content</h2>
          <div className="flex items-center space-x-2">
            <Icon 
              name={getPlatformIcon(content?.platform)} 
              size={16} 
              className={getPlatformColor(content?.platform)} 
            />
            <span className="text-sm font-medium text-muted-foreground">{content?.platform}</span>
          </div>
        </div>
        
        <div className="space-y-3">
          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
            <div className="flex items-center space-x-1">
              <Icon name="Calendar" size={14} />
              <span>Scheduled: {formatDate(content?.scheduledDate)}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="User" size={14} />
              <span>By: {content?.author}</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-4 text-sm">
            <div className="flex items-center space-x-1">
              <Icon name="Target" size={14} className="text-muted-foreground" />
              <span className="text-muted-foreground">Campaign:</span>
              <span className="font-medium text-foreground">{content?.campaign}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-muted-foreground mb-2 block">Content Text</label>
            <div className="bg-muted/30 border border-border rounded-md p-4 min-h-[200px]">
              <div 
                className="text-foreground leading-relaxed whitespace-pre-wrap"
                dangerouslySetInnerHTML={{ __html: content?.text?.replace(/\n/g, '<br>') }}
              />
            </div>
          </div>

          {content?.media && content?.media?.length > 0 && (
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-2 block">Attached Media</label>
              <div className="grid grid-cols-2 gap-3">
                {content?.media?.map((media, index) => (
                  <div key={index} className="relative bg-muted/30 border border-border rounded-md p-3">
                    <div className="flex items-center space-x-2">
                      <Icon 
                        name={media?.type === 'image' ? 'Image' : 'Video'} 
                        size={16} 
                        className="text-muted-foreground" 
                      />
                      <span className="text-sm text-foreground truncate">{media?.name}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {media?.size} • {media?.type}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {content?.hashtags && content?.hashtags?.length > 0 && (
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-2 block">Hashtags</label>
              <div className="flex flex-wrap gap-2">
                {content?.hashtags?.map((tag, index) => (
                  <span 
                    key={index}
                    className="inline-flex items-center px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-md"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="pt-4 border-t border-border">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Content ID: <span className="font-mono text-foreground">{content?.id}</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                iconName="Edit"
                iconPosition="left"
                onClick={() => onContentUpdate && onContentUpdate(content?.id)}
              >
                Edit Content
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentPanel;